import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import NotyfContext from "../../contexts/NotyfContext";
import { useWhitelabel } from "../../contexts/WhitelabelContext";
import {
  getBuyerPurchaseOrder,
  getSellerPurchaseOrders,
  updatePurchaseOrderStatus,
} from "../../redux/slices/purchaseOrders";
import useDeviceType from "../../hooks/useDeviceType";
import getPurchaseOrderData from "./purchaseOrderData";
import DefaultColumnFilter from "../../utils/DefaultColumnFilter";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import {
  ButtonGroup,
  Card,
  Col,
  Container,
  DropdownButton,
  Form,
  Pagination,
  Row,
  Table,
  Tabs,
  Tab,
  Dropdown,
} from "react-bootstrap";
import FiltersCard from "../../utils/FiltersCard";
import {
  useFilters,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import { Helmet } from "react-helmet-async";
import Loader from "../../components/spinners/Loader";
import { ro } from "date-fns/locale";

const PurchaseOrdersHistory = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const buyerPurchaseOrders = useSelector(
    (state) => state.purchaseOrders.buyerPurchaseOrders
  );
  const sellerPurchaseOrders = useSelector(
    (state) => state.purchaseOrders.sellerPurchaseOrders
  );
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const notyf = useContext(NotyfContext);
  const [activeUser, setActiveUser] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const {
    whitelabelLoading,
    faviconUrl,
    whitelabelUrl,
    fontColor,
    backgroundColor,
  } = useWhitelabel();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const { purchaseOrderColumns } = useMemo(() => getPurchaseOrderData(t), [t]);
  const [activeTab, setActiveTab] = useState("selling");

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [impersonationUser, user]);
  // Which tabs the user may see
  const canViewSelling = useMemo(
    () => ["admin", "tenant", "manager"].includes(activeUser?.role),
    [activeUser?.role]
  );
  const canViewBuying = useMemo(
    () => ["customer", "tenant", "manager"].includes(activeUser?.role),
    [activeUser?.role]
  );

  // Pick a sensible default tab when role becomes known
  useEffect(() => {
    if (!activeUser?.role) return;
    if (canViewSelling && !canViewBuying) setActiveTab("selling");
    else if (!canViewSelling && canViewBuying) setActiveTab("buying");
    else if (canViewSelling && canViewBuying) setActiveTab("selling"); // both available -> start with selling
  }, [activeUser?.role, canViewSelling, canViewBuying]);

  useEffect(() => {
    const fetchSellerPurchaseOrders = async () => {
      await dispatch(getSellerPurchaseOrders()).catch((error) => {
        notyf.open({
          type: "danger",
          message: "Something went wrong while fetching purchase orders.",
          duration: 5000,
          dismissible: false,
          ripple: true,
          position: {
            x: "center",
            y: "top",
          },
        });
      });
    };

    const fetchBuyerPurchaseOrders = async () => {
      await dispatch(getBuyerPurchaseOrder()).catch((error) => {
        notyf.open({
          type: "danger",
          message: "Something went wrong while fetching purchase orders.",
          duration: 5000,
          dismissible: false,
          ripple: true,
          position: {
            x: "center",
            y: "top",
          },
        });
      });
    };

    if (activeUser.id) {
      if (
        activeUser.role === "customer" ||
        activeUser.role === "manager" ||
        activeUser.role === "tenant"
      ) {
        fetchBuyerPurchaseOrders();
      }
      if (
        activeUser.role === "admin" ||
        activeUser.role === "tenant" ||
        activeUser.role === "manager"
      ) {
        fetchSellerPurchaseOrders();
      }
      setTimeout(() => {
        setIsLoading(false);
      }, 3500);
    }
  }, [activeUser, dispatch, notyf]);

  const filterTypes = React.useMemo(
    () => ({
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const data = useMemo(() => {
    return activeTab === "buying"
      ? buyerPurchaseOrders ?? []
      : sellerPurchaseOrders ?? [];
  }, [activeTab, buyerPurchaseOrders, sellerPurchaseOrders]);

  const baseColumns = purchaseOrderColumns;

  const actionsColumn = useMemo(
    () => ({
      id: "Actions",
      Header: t("emails.actions"),
      disableSortBy: true, // react-table v7 prop
      Cell: ({ row }) => {
        if (
          row.original.status === "COMPLETED" ||
          row.original.status === "CANCELLED"
        ) {
          return null;
        }
        return (
          <DropdownButton
            as={ButtonGroup}
            title="Actions"
            variant="primary"
            data-boundary="viewport"
            style={{
              "--dynamic-bg-color": backgroundColor ?? "#0E1A2B",
              "--dynamic-font-color": fontColor ?? "#ffffff",
              border: "none",
            }}
          >
            {/* This now updates correctly when activeTab changes */}
            {activeTab === "selling" && row.original.status === "NEW" && (
              <Dropdown.Item
                eventKey="inProgress"
                onClick={async () => {
                  try {
                    await dispatch(
                      updatePurchaseOrderStatus({
                        id: row.original.id,
                        status: "IN_PROGRESS",
                        category: "selling",
                      })
                    );
                    notyf.open({
                      type: "success",
                      message: "Purchase order status updated.",
                      duration: 5000,
                      dismissible: false,
                      ripple: true,
                      position: { x: "center", y: "top" },
                    });
                  } catch (e) {
                    notyf.open({
                      type: "danger",
                      message: "Failed to update purchase order status.",
                      duration: 5000,
                      dismissible: false,
                      ripple: true,
                      position: { x: "center", y: "top" },
                    });
                  }
                }}
              >
                In Progress
              </Dropdown.Item>
            )}
            {activeTab === "selling" && row.original.status === "IN_PROGRESS" && (
              <Dropdown.Item
                eventKey="completed"
                onClick={async () => {
                  try {
                    await dispatch(
                      updatePurchaseOrderStatus({
                        id: row.original.id,
                        status: "COMPLETED",
                        category: "selling",
                      })
                    );
                    notyf.open({
                      type: "success",
                      message: "Purchase order status updated.",
                      duration: 5000,
                      dismissible: false,
                      ripple: true,
                      position: { x: "center", y: "top" },
                    });
                  } catch (e) {
                    notyf.open({
                      type: "danger",
                      message: "Failed to update purchase order status.",
                      duration: 5000,
                      dismissible: false,
                      ripple: true,
                      position: { x: "center", y: "top" },
                    });
                  }
                }}
              >
                Completed
              </Dropdown.Item>
            )}

            {row.original.status !== "COMPLETED" && (
              <Dropdown.Item
                eventKey="cancel"
                onClick={async () => {
                  try {
                    await dispatch(
                      updatePurchaseOrderStatus({
                        id: row.original.id,
                        status: "CANCELLED",
                        category: activeTab === "buying" ? "buying" : "selling",
                      })
                    );
                    notyf.open({
                      type: "success",
                      message: "Purchase order status updated.",
                      duration: 5000,
                      dismissible: false,
                      ripple: true,
                      position: { x: "center", y: "top" },
                    });
                  } catch (e) {
                    notyf.open({
                      type: "danger",
                      message: "Failed to update purchase order status.",
                      duration: 5000,
                      dismissible: false,
                      ripple: true,
                      position: { x: "center", y: "top" },
                    });
                  }
                }}
              >
                Cancel
              </Dropdown.Item>
            )}
          </DropdownButton>
        );
      },
    }),
    [activeTab, backgroundColor, fontColor, t, dispatch, notyf]
  );

  const columns = useMemo(
    () => [...baseColumns, actionsColumn],
    [baseColumns, actionsColumn]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    prepareRow,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: columns,
      data: data,
      initialState: { pageIndex: 0 },
      defaultColumn,
      filterTypes,
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect
  );

  if (isLoading) {
    return (
      <React.Fragment>
        <Helmet title="Purchase Orders History" />
        <Container fluid className="p-0">
          <Card>
            <Card.Body>
              <Loader />
            </Card.Body>
          </Card>
        </Container>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Helmet title="Purchase Orders History">
        <link rel="icon" href={faviconUrl} />
      </Helmet>
      <Container fluid className="p-0">
        <FiltersCard headerGroups={headerGroups} />
        <Card>
          {" "}
          <Card.Header
            style={{
              // theme vars (fallbacks included)
              "--po-accent": backgroundColor ?? "#0E1A2B",
              "--po-on-accent": fontColor ?? "#ffffff",
              "--po-bg": "#f8f9fa",
              "--po-fg": "#495057",
              "--po-border": "rgba(0,0,0,0.12)",
              "--po-ring": "rgba(0,0,0,0.12)",
            }}
          >
            <Row className="align-items-center">
              <Col xs="12" className={isDesktop ? "mb-3" : ""}>
                <Card.Title className="mb-2">Purchase Orders</Card.Title>
              </Col>
              <Col xs="12">
                <Tabs
                  id="po-tabs"
                  variant="pills"
                  activeKey={activeTab}
                  onSelect={(k) => k && setActiveTab(k)}
                  mountOnEnter
                  unmountOnExit
                  className="po-tabs"
                >
                  {canViewSelling && (
                    <Tab eventKey="selling" title="Selling" className="me-5" />
                  )}
                  {canViewBuying && <Tab eventKey="buying" title="Buying" />}
                </Tabs>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body>
            <Table id="emailsTable" hover responsive {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps()}>
                        <span {...column.getSortByToggleProps()}>
                          {column.render("Header")}
                          <span>
                            {column.sortable ? (
                              column.isSorted ? (
                                column.isSortedDesc ? (
                                  <FaSortUp className="ms-lg-2 ms-xl-2" />
                                ) : (
                                  <FaSortDown className="ms-lg-2 ms-xl-2" />
                                )
                              ) : (
                                <FaSort className="ms-lg-2 ms-xl-2" />
                              )
                            ) : null}
                          </span>
                        </span>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row, i) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return (
                          <td {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            {isDesktop && (
              <Row>
                <Col md="8">
                  <span className="mx-2">
                    {t("table.page")}{" "}
                    <strong>
                      {pageIndex + 1} {t("table.of")} {pageOptions.length}
                    </strong>
                  </span>
                  <span className="ms-3 me-2">{t("table.show")}:</span>
                  <Form.Select
                    className="d-inline-block w-auto"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                    }}
                  >
                    {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </Form.Select>

                  <span className="ms-3 me-2">{t("table.goToPage")}:</span>
                  <Form.Control
                    className="d-inline-block"
                    type="number"
                    defaultValue={pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value
                        ? Number(e.target.value) - 1
                        : 0;
                      gotoPage(page);
                    }}
                    style={{ width: "75px" }}
                  />
                </Col>
                <Col md="4">
                  <Pagination className="float-end">
                    <Pagination.First
                      onClick={() => {
                        gotoPage(0);
                      }}
                      disabled={!canPreviousPage}
                    />
                    <Pagination.Prev
                      onClick={() => {
                        previousPage();
                      }}
                      disabled={!canPreviousPage}
                    />
                    <Pagination.Next
                      onClick={() => {
                        nextPage();
                      }}
                      disabled={!canNextPage}
                    />
                    <Pagination.Last
                      onClick={() => {
                        gotoPage(pageCount - 1);
                      }}
                      disabled={!canNextPage}
                    />
                  </Pagination>
                </Col>
              </Row>
            )}
            {!isDesktop && (
              <React.Fragment>
                <div className="emails-pagination mt-3">
                  <Row className="mb-3">
                    <Col className="d-flex justify-content-center">
                      <Pagination className="float-end">
                        <Pagination.Prev
                          onClick={() => previousPage()}
                          disabled={!canPreviousPage}
                        />
                        <Col className="d-flex align-items-end mx-2 px-3">
                          <span className="mx-2">
                            <strong>
                              {pageIndex + 1} {t("table.of")}{" "}
                              {pageOptions.length}
                            </strong>
                          </span>
                        </Col>
                        <Pagination.Next
                          onClick={() => nextPage()}
                          disabled={!canNextPage}
                        />
                      </Pagination>
                    </Col>
                  </Row>
                  <hr />
                  <Row>
                    <Col xs="auto">
                      <span className="me-2">{t("table.show")}:</span>
                      <Form.Select
                        className="d-inline-block w-auto"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                        }}
                        style={{
                          padding: "0.25rem 2.1rem 0.25rem 0.7rem",
                          border: "none",
                          boxShadow: "none",
                          fontSize: "14px",
                        }}
                      >
                        {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                          <option key={pageSize} value={pageSize}>
                            {pageSize}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col>
                      <div className="float-end">
                        <span className="me-2">{t("table.goToPage")}:</span>
                        <Form.Control
                          className="d-inline-block w-auto"
                          type="number"
                          defaultValue={pageIndex + 1}
                          min={1}
                          max={pageCount}
                          onChange={(e) => {
                            const page = e.target.value
                              ? Number(e.target.value) - 1
                              : 0;
                            gotoPage(page);
                          }}
                          style={{
                            padding: "0.25rem 1.2rem 0.25rem 0.7rem",
                            fontSize: "0.825rem",
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              </React.Fragment>
            )}
          </Card.Body>
        </Card>
      </Container>
    </React.Fragment>
  );
};

export default PurchaseOrdersHistory;
